import {FIELD_MAPPING_TYPE, SETTINGS_OF_PLATFORM} from 'src/interfaces/Platform.interface'

export const SETTING_FILE_INPUT_TYPES = ['DOWNLOADFILE', 'UPLOADFILE'] as const

export type SettingFileInputType = (typeof SETTING_FILE_INPUT_TYPES)[number]

export const normalizeSettingFileInputType = (
  inputType?: string | null
): SettingFileInputType | null => {
  if (!inputType) return null

  const normalized = inputType.trim().toUpperCase()

  if (normalized === 'DONWLOADFILE' || normalized === 'DOWNLOADFILE') {
    return 'DOWNLOADFILE'
  }

  if (normalized === 'UPLOADFILE') {
    return 'UPLOADFILE'
  }

  return null
}

export const isSettingFileChildInputType = (inputType?: string | null) =>
  normalizeSettingFileInputType(inputType) !== null

export const isSettingFileDownloadInputType = (inputType?: string | null) =>
  normalizeSettingFileInputType(inputType) === 'DOWNLOADFILE'

export const isSettingFileUploadInputType = (inputType?: string | null) =>
  normalizeSettingFileInputType(inputType) === 'UPLOADFILE'

export const isSettingFileActionMapping = (fieldMapping: FIELD_MAPPING_TYPE | null | undefined) =>
  Boolean(normalizeSettingFileInputType(fieldMapping?.input_type))

export const partitionSettingChildren = (children: SETTINGS_OF_PLATFORM[] = []) => {
  const fileActionChildren = children.filter((child) => isSettingFileChildInputType(child.input_type))
  const standardChildren = children.filter((child) => !isSettingFileChildInputType(child.input_type))

  return {fileActionChildren, standardChildren}
}

export const getSettingFileActionMappings = (
  setting: {field_mappings?: FIELD_MAPPING_TYPE[]; children?: {field_mappings?: FIELD_MAPPING_TYPE[]}[]} | null | undefined
): FIELD_MAPPING_TYPE[] => {
  if (!setting) return []

  const fromSetting = (setting.field_mappings ?? []).filter(isSettingFileActionMapping)
  const fromChildren = (setting.children ?? []).flatMap((child) =>
    (child.field_mappings ?? []).filter(isSettingFileActionMapping)
  )

  return [...fromSetting, ...fromChildren]
}

export const getSettingFileActionMappingsData = (mappingsData: FIELD_MAPPING_TYPE[] = []) =>
  mappingsData.filter(isSettingFileActionMapping)

export const getSettingFileMappingsForCallout = (
  setting: {
    field_mappings?: FIELD_MAPPING_TYPE[]
    mappings_data?: FIELD_MAPPING_TYPE[]
    children?: SETTINGS_OF_PLATFORM[]
  } | null | undefined
) => {
  const fromFieldMappings = getSettingFileActionMappings(setting)

  if (getDirectSettingFileChildActions(setting?.children ?? []).length > 0) {
    return fromFieldMappings
  }

  return [...fromFieldMappings, ...getSettingFileActionMappingsData(setting?.mappings_data ?? [])]
}

export const getDirectSettingFileChildActions = (children: SETTINGS_OF_PLATFORM[] = []) =>
  children.filter((child) => isSettingFileChildInputType(child.input_type))

export const getIdentifierSetting = (settings: SETTINGS_OF_PLATFORM[] = []) =>
  settings.find((setting) => setting?.group_code === 'IDENTIFIER') ?? null

// DOWNLOADFILE/UPLOADFILE children of IDENTIFIER; the identifier row renders these first.
export const getIdentifierSettingFileChildActions = (settings: SETTINGS_OF_PLATFORM[] = []) =>
  getDirectSettingFileChildActions(getIdentifierSetting(settings)?.children ?? [])

export const hasIdentifierSettingFileActions = (settings: SETTINGS_OF_PLATFORM[] = []) =>
  getIdentifierSettingFileChildActions(settings).length > 0

export const hasSettingFileActions = (
  setting: {
    field_mappings?: FIELD_MAPPING_TYPE[]
    mappings_data?: FIELD_MAPPING_TYPE[]
    children?: SETTINGS_OF_PLATFORM[]
  } | null | undefined,
  additionalChildSettings: SETTINGS_OF_PLATFORM[] = []
) =>
  getSettingFileActionMappings(setting).length > 0 ||
  getSettingFileActionMappingsData(setting?.mappings_data ?? []).length > 0 ||
  getDirectSettingFileChildActions(setting?.children ?? []).length > 0 ||
  getDirectSettingFileChildActions(additionalChildSettings).length > 0

export const partitionFieldMappings = (fieldMappings: FIELD_MAPPING_TYPE[] = []) => {
  const fileActionMappings = fieldMappings.filter(isSettingFileActionMapping)
  const standardMappings = fieldMappings.filter((mapping) => !isSettingFileActionMapping(mapping))

  return {fileActionMappings, standardMappings}
}

export const getSettingFileMappingIds = (fieldMapping: FIELD_MAPPING_TYPE) => ({
  available_platform_setting_id:
    (fieldMapping as FIELD_MAPPING_TYPE & {available_platform_setting?: number}).available_platform_setting ??
    null,
  setting_mapping_id: fieldMapping.setting_mapping_id ?? fieldMapping.id ?? null,
})

export const buildManageSettingFileParamsFromMapping = (
  fieldMapping: FIELD_MAPPING_TYPE,
  platformId: number,
  parentAvailablePlatformSettingId?: number | null
) => {
  const ids = getSettingFileMappingIds(fieldMapping)
  const available_platform_setting_id =
    ids.available_platform_setting_id ?? parentAvailablePlatformSettingId ?? null

  if (!platformId || !available_platform_setting_id) {
    return null
  }

  return {
    platform_id: platformId,
    available_platform_setting_id,
    ...(ids.setting_mapping_id ? {setting_mapping_id: ids.setting_mapping_id} : {}),
  }
}

export const buildManageSettingFileParamsFromChildSetting = (
  childItem: SETTINGS_OF_PLATFORM,
  platformId: number
) => {
  if (!platformId || !childItem.id) {
    return null
  }

  return {
    platform_id: platformId,
    available_platform_setting_id: childItem.id,
  }
}

export const getSettingFilePanelMeta = ({
  mappings = [],
  childSettings = [],
  panelTitle,
  panelDescription,
}: {
  mappings?: FIELD_MAPPING_TYPE[]
  childSettings?: SETTINGS_OF_PLATFORM[]
  panelTitle?: string
  panelDescription?: string
}) => {
  if (panelTitle || panelDescription) {
    return {
      title: panelTitle ?? '',
      description: panelDescription ?? '',
    }
  }

  const richMapping = mappings.find(
    (mapping) =>
      mapping.description &&
      (mapping.description.includes('<') || mapping.description.length > mapping.display_name.length + 12)
  )

  if (richMapping) {
    return {
      title: richMapping.display_name ?? '',
      description: richMapping.description ?? '',
    }
  }

  const richChild =
    childSettings.find(
      (child) =>
        isSettingFileDownloadInputType(child.input_type) &&
        child.description &&
        (child.description.includes('<') || child.description.length > (child.display_name?.length ?? 0) + 12)
    ) ??
    childSettings.find(
      (child) =>
        child.description &&
        (child.description.includes('<') || child.description.length > (child.display_name?.length ?? 0) + 12)
    )

  if (richChild) {
    return {
      title: richChild.display_name ?? '',
      description: richChild.description ?? '',
    }
  }

  if (mappings[0]) {
    return {
      title: mappings[0].display_name ?? '',
      description: mappings[0].description ?? '',
    }
  }

  return {title: '', description: ''}
}

export const getSettingFileActionButtonLabel = ({
  displayName = '',
  placeholder = '',
  panelTitle = '',
  isDownload,
  isSingleDownloadOnly = false,
}: {
  displayName?: string | null
  placeholder?: string | null
  panelTitle?: string
  isDownload: boolean
  isSingleDownloadOnly?: boolean
}) => {
  const normalizedDisplayName = displayName?.trim() ?? ''
  const normalizedPlaceholder = placeholder?.trim() ?? ''
  const normalizedPanelTitle = panelTitle?.trim() ?? ''
  const repeatsPanelTitle =
    normalizedPanelTitle.length > 0 &&
    (normalizedPlaceholder === normalizedPanelTitle ||
      normalizedDisplayName === normalizedPanelTitle)

  if (repeatsPanelTitle) {
    if (isSingleDownloadOnly && isDownload) {
      return 'Download'
    }

    return isDownload ? 'Download Template' : 'Upload Mapping'
  }

  if (
    normalizedPlaceholder &&
    normalizedPlaceholder !== normalizedDisplayName &&
    normalizedPlaceholder.length <= normalizedDisplayName.length + 8
  ) {
    return normalizedPlaceholder
  }

  if (normalizedPlaceholder) {
    return normalizedPlaceholder
  }

  return isDownload ? 'Download' : 'Upload'
}

export const getSettingFileRowLabel = ({
  displayName = '',
  panelTitle = '',
  isDownload,
}: {
  displayName?: string | null
  panelTitle?: string
  isDownload: boolean
}) => {
  const normalizedDisplayName = displayName?.trim() ?? ''
  const normalizedPanelTitle = panelTitle?.trim() ?? ''

  if (normalizedPanelTitle && normalizedDisplayName === normalizedPanelTitle) {
    return isDownload ? 'Download Template' : 'Upload Mapping'
  }

  return normalizedDisplayName || (isDownload ? 'Download' : 'Upload')
}
